import { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { LIEN_NOTICE_DELIVERY, lienNoticeItems, paths, type Agreement, type Job, type LienNoticeItem, type Project } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import type { LotRow } from "../lib/lots";
import { PresentAgreement } from "./JobAgreements";

// Project → Lien notices (Nevada): when we're a sub (not hired by the owner),
// each lot needs a Notice of Right to Lien within 31 days of our first work
// there to keep lien rights for all of it. One line per lot: when work
// started, when the notice is due, and prepare → sign → mark sent.

type Notice = Agreement & { id: string };
const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const STATUS: Record<LienNoticeItem["status"], { label: string; cls: string }> = {
  not_started: { label: "No work yet", cls: "text-steel" },
  due: { label: "Due", cls: "text-ink" },
  due_soon: { label: "Due soon", cls: "font-semibold text-brandorange-dark" },
  overdue: { label: "Past 31 days", cls: "font-semibold text-danger" },
  prepared: { label: "Ready to sign and send", cls: "text-brandorange-dark" },
  sent: { label: "Sent", cls: "text-success" },
};

export function LienNotices({
  tenantId,
  projectId,
  project,
  jobs,
  lots,
  customerName,
  canEdit,
}: {
  tenantId: string;
  projectId: string;
  project: Project;
  jobs: Array<Job & { id: string }>;
  lots: LotRow[];
  customerName: string;
  canEdit: boolean;
}) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [preparing, setPreparing] = useState<LienNoticeItem | null>(null);
  const [form, setForm] = useState({ owner: "", work: "", property: "", contractWith: "" });
  const [sending, setSending] = useState<string | null>(null);
  const [how, setHow] = useState("Certified mail");
  const [signing, setSigning] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    void getDoc(doc(db, paths.tenant(tenantId))).then((s) => setCompanyName((s.get("name") as string) ?? ""));
    return onSnapshot(
      query(collection(db, paths.tenantAgreements(tenantId)), where("projectId", "==", projectId)),
      (s) => setNotices(s.docs.map((d) => ({ id: d.id, ...(d.data() as Agreement) })).filter((a) => a.lienDoc === "notice")),
      () => setNotices([]),
    );
  }, [tenantId, projectId]);

  const items = useMemo(
    () =>
      lienNoticeItems({
        lots: lots.map((l) => ({ id: l.id, name: l.name })),
        jobs,
        notices: notices.map((n) => ({ id: n.id, lotId: n.lotId ?? null, sentAt: n.sentAt ?? null, createdAt: n.createdAt, status: n.status })),
        now: Date.now(),
      }),
    [lots, jobs, notices],
  );

  if (!project.lienNotices) {
    return canEdit ? (
      <Card className="flex flex-wrap items-center justify-between gap-2 py-3">
        <p className="text-xs text-steel">
          <span className="font-semibold text-ink">Lien notices (Nevada).</span> If you're a sub here (hired by a builder, not the owner), Clevis can track the Notice of Right to Lien each lot needs within 31 days of first work.
        </p>
        <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => void updateDoc(doc(db, paths.tenantProject(tenantId, projectId)), { lienNotices: true, updatedAt: Date.now() })}>
          Track lien notices
        </Button>
      </Card>
    ) : null;
  }

  function startPrepare(it: LienNoticeItem) {
    const lot = lots.find((l) => l.id === it.lotId);
    const items = [...new Set(jobs.filter((j) => (it.lotId ? j.lotId === it.lotId : true)).map((j) => j.projectItem?.name || j.jobType || "").filter(Boolean))];
    setForm({
      owner: "",
      work: items.length ? `${items.join(", ")}: materials and installation` : "Materials and installation",
      property: lot?.address || [lot?.name, project.name].filter(Boolean).join(", ") || project.address || project.name,
      contractWith: customerName,
    });
    setPreparing(it);
    setError("");
  }
  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const pdf = (n: Notice) =>
    act(async () => {
      const res = await call<{ base64: string; filename: string }>("getAgreementPdf", { tenantId, agreementId: n.id }, { mutation: false });
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${res.base64}`;
      a.download = res.filename;
      a.click();
    });

  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
        <CardTitle className="mb-0">Lien notices (Nevada)</CardTitle>
        {canEdit ? (
          <button className="text-xs text-steel underline hover:text-ink" onClick={() => void updateDoc(doc(db, paths.tenantProject(tenantId, projectId)), { lienNotices: false, updatedAt: Date.now() })}>
            Stop tracking
          </button>
        ) : null}
      </div>
      <p className="px-4 pt-1 text-xs text-steel">{LIEN_NOTICE_DELIVERY}</p>
      <div className="mt-2">
        {items.map((it) => {
          const n = notices.find((x) => x.id === it.noticeId);
          return (
            <div key={it.key} className="border-b border-steel/10 last:border-0">
              <div className="flex flex-wrap items-center gap-2 px-4 py-1.5 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-ink">{it.label}</span>
                  <span className="block text-xs text-steel">
                    {it.firstWorkAt ? `First work ${day(it.firstWorkAt)} · send by ${day(it.dueAt!)}` : "Starts counting at the first job"}
                    {it.sentAt ? ` · sent ${day(it.sentAt)}${n?.sentHow ? ` (${n.sentHow})` : ""}` : ""}
                  </span>
                </span>
                <span className={cn("text-xs", STATUS[it.status].cls)}>{STATUS[it.status].label}</span>
                {!n ? (
                  <Button variant="ghost" className="px-2.5 py-0.5 text-xs" onClick={() => startPrepare(it)}>
                    Prepare notice
                  </Button>
                ) : n.status === "pending" ? (
                  <Button className="px-2.5 py-0.5 text-xs" onClick={() => setSigning(n)}>
                    Sign for company
                  </Button>
                ) : !n.sentAt ? (
                  <Button className="px-2.5 py-0.5 text-xs" onClick={() => setSending(sending === n.id ? null : n.id)}>
                    Mark sent
                  </Button>
                ) : null}
                {n ? (
                  <button className="text-xs text-steel underline hover:text-ink" onClick={() => void pdf(n)}>
                    PDF
                  </button>
                ) : null}
              </div>
              {it.status === "overdue" ? (
                <p className="px-4 pb-1.5 text-[11px] text-danger">Past the 31 days: a notice sent now keeps rights only for work from the 31 days before it's sent. Send it as soon as you can.</p>
              ) : null}
              {preparing?.key === it.key ? (
                <div className="grid gap-2 bg-cream/40 px-4 py-2 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label>Owner's name and address (the property owner)</Label>
                    <Input className="py-1 text-sm" value={form.owner} placeholder="e.g. BestBuilder Bros, LLC, 100 Main St, Las Vegas, NV 89101" onChange={(e) => setForm({ ...form, owner: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>General description of materials, equipment, work or services</Label>
                    <Input className="py-1 text-sm" value={form.work} onChange={(e) => setForm({ ...form, work: e.target.value })} />
                  </div>
                  <div>
                    <Label>Property (description or street address)</Label>
                    <Input className="py-1 text-sm" value={form.property} onChange={(e) => setForm({ ...form, property: e.target.value })} />
                  </div>
                  <div>
                    <Label>Under contract with (who hired you)</Label>
                    <Input className="py-1 text-sm" value={form.contractWith} onChange={(e) => setForm({ ...form, contractWith: e.target.value })} />
                  </div>
                  <div className="flex gap-2 sm:col-span-2">
                    <Button
                      className="px-3 py-1 text-xs"
                      busy={busy}
                      disabled={form.owner.trim().length < 3 || form.work.trim().length < 3 || form.property.trim().length < 3 || form.contractWith.trim().length < 2}
                      onClick={() =>
                        void act(async () => {
                          await call("createLienNotice", { tenantId, projectId, lotId: it.lotId, ownerNameAddress: form.owner, workDescription: form.work, property: form.property, contractWith: form.contractWith });
                          setPreparing(null);
                        })
                      }
                    >
                      Prepare it
                    </Button>
                    <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => setPreparing(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : null}
              {n && sending === n.id ? (
                <div className="flex flex-wrap items-end gap-2 bg-cream/40 px-4 py-2">
                  <div className="min-w-56 flex-1">
                    <Label>How it went (certified mail number, or "hand delivered")</Label>
                    <Input className="py-1 text-sm" value={how} maxLength={200} onChange={(e) => setHow(e.target.value)} />
                  </div>
                  <Button
                    className="px-3 py-1 text-xs"
                    busy={busy}
                    disabled={how.trim().length < 2}
                    onClick={() =>
                      void act(async () => {
                        await call("markLienNoticeSent", { tenantId, agreementId: n.id, how });
                        setSending(null);
                      })
                    }
                  >
                    Mark sent today
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {error ? <p className="px-4 pb-2 text-xs text-danger">{error}</p> : null}
      <p className="px-4 pb-3 text-[11px] text-steel">Nevada's form (NRS 108.245). Not legal advice; your attorney can confirm deadlines on a specific job.</p>
      {signing ? <PresentAgreement tenantId={tenantId} agreement={signing} companyName={companyName} onExit={() => setSigning(null)} /> : null}
    </Card>
  );
}
